import { Button, Modal } from "@xola/ui-kit";
import React, { useState } from "react";
import { ProductObject, SelectionState } from "types";
import { ProductOrCatalogSelector, ProductOrCatalogSelectorProps } from "./ProductOrCatalogSelector";
import { SecondaryButton } from "./SecondaryButton";

type AllowedProductOrCatalogSelectorProps = Omit<ProductOrCatalogSelectorProps, "value" | "onChange">;

interface ProductOrCatalogSelectorModalProps extends AllowedProductOrCatalogSelectorProps {
    isOpen: boolean;
    onClose: () => void;
    value: SelectionState;
    onConfirm: (value: SelectionState) => void;
    title?: string;
    description?: string;
    productObjects?: ProductObject[];
    defaultEnabledProductObjects?: ProductObject[];
    allowEmptySelection?: boolean;
}

export const ProductOrCatalogSelectorModal = ({
    isOpen,
    onClose,
    value,
    onConfirm,
    title = "Select Items",
    description,
    productObjects,
    defaultEnabledProductObjects,
    allowEmptySelection = false,
    ...productOrCatalogSelectorProps
}: ProductOrCatalogSelectorModalProps) => {
    return (
        <Modal isOpen={isOpen} size="huge" onClose={onClose}>
            <ModalContent
                allowEmptySelection={allowEmptySelection}
                value={value}
                title={title}
                description={description}
                productObjects={productObjects}
                defaultEnabledProductObjects={defaultEnabledProductObjects}
                onConfirm={onConfirm}
                onClose={onClose}
                {...productOrCatalogSelectorProps}
            />
        </Modal>
    );
};

interface ModalContentProps extends Omit<ProductOrCatalogSelectorModalProps, "isOpen"> {}

const ModalContent = ({
    onClose,
    value,
    onConfirm,
    title,
    description,
    productObjects,
    defaultEnabledProductObjects,
    allowEmptySelection,
    ...productOrCatalogSelectorProps
}: ModalContentProps) => {
    const [selectedProducts, setSelectedProducts] = useState<SelectionState>(value);

    const handleConfirmClick = () => {
        onConfirm(selectedProducts);
        onClose();
    };

    const isEmptySelection = !selectedProducts.all && selectedProducts.selected.length === 0;

    return (
        <>
            <Modal.Header description={description}>{title}</Modal.Header>

            <Modal.Body>
                <ProductOrCatalogSelector
                    value={value}
                    productObjects={productObjects}
                    defaultEnabledProductObjects={defaultEnabledProductObjects}
                    onChange={setSelectedProducts}
                    {...productOrCatalogSelectorProps}
                />
            </Modal.Body>

            <Modal.Footer className="space-x-4">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                <Button disabled={!allowEmptySelection && isEmptySelection} onClick={handleConfirmClick}>
                    Apply
                </Button>
            </Modal.Footer>
        </>
    );
};
